import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Users, ThumbsUp, CheckCircle, Loader2 } from "lucide-react";

const CityVoteModal = ({ 
  isOpen, 
  onClose, 
  pickupLocation, 
  cityName,
  customer 
}) => {
  const [voteCount, setVoteCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [voted, setVoted] = useState(false);
  const [fetchingCount, setFetchingCount] = useState(true);

  // Fetch current vote count when modal opens
  useEffect(() => {
    if (isOpen && pickupLocation) {
      fetchVoteCount();
    }
  }, [isOpen, pickupLocation]);

  const fetchVoteCount = async () => {
    setFetchingCount(true);
    try {
      const params = new URLSearchParams();
      if (cityName) {
        params.append("city_name", cityName);
      } else if (pickupLocation?.lat && pickupLocation?.lng) {
        params.append("lat", pickupLocation.lat);
        params.append("lng", pickupLocation.lng);
      }
      
      const res = await axios.get(`${API}/votes/count?${params.toString()}`);
      setVoteCount(res.data.total_votes || 0);
    } catch (err) {
      console.error("Failed to fetch vote count:", err);
    } finally {
      setFetchingCount(false);
    }
  };

  const handleVote = async () => {
    if (!customer?.id || !pickupLocation) {
      toast.error("Unable to record vote. Please try again.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/votes/vote`, {
        customer_id: customer.id,
        customer_phone: customer.phone,
        lat: pickupLocation.lat,
        lng: pickupLocation.lng,
        city_name: cityName || "Unknown Area",
        address: pickupLocation.address
      });

      setVoted(true);
      setVoteCount(res.data.total_votes);
      toast.success("Thanks for voting! We'll notify you when we launch in your area.");
    } catch (err) {
      console.error("Failed to record vote:", err);
      toast.error("Failed to record your vote. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const displayCity = cityName || "your area";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="city-vote-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <MapPin className="w-5 h-5 text-amber-500" />
            No Drivers Available Yet
          </DialogTitle>
          <DialogDescription>
            BizilGO isn't available in {displayCity} yet, but we're expanding!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Vote Count Display */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
              <Users className="w-5 h-5" />
              <span className="text-sm font-medium">Customer Interest</span>
            </div>
            {fetchingCount ? (
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" />
            ) : (
              <>
                <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                  {voteCount}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {voteCount === 1 ? "customer wants" : "customers want"} BizilGO in {displayCity}
                </p>
              </>
            )}
          </div>

          {/* Vote Button or Success Message */}
          {voted ? (
            <div className="text-center space-y-3">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-lg font-semibold text-green-600">Thanks for your vote!</p>
              <p className="text-sm text-muted-foreground">
                We'll notify you when BizilGO launches in {displayCity}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-center text-muted-foreground">
                Want BizilGO in your city? Vote to help us prioritize where to launch next!
              </p>
              <Button 
                onClick={handleVote} 
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                size="lg"
                data-testid="vote-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ThumbsUp className="w-4 h-4 mr-2" />
                )}
                {loading ? "Recording Vote..." : "Vote for BizilGO in My City"}
              </Button>
            </div>
          )}

          {/* Close Button */}
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="w-full"
          >
            {voted ? "Close" : "Maybe Later"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CityVoteModal;
